/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, {
	forwardRef,
	type Ref,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { bindAll } from 'bind-event-listener';
import { defineMessages, useIntl } from 'react-intl';

import { getDocument } from '@atlaskit/browser-apis';
import { cssMap, jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { ExitingPersistence } from '@atlaskit/motion';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Pressable } from '@atlaskit/primitives/compiled';
import { Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import {
	type ConversationAction,
	HandleActionType,
} from '@atlassian/rovo-conversation-actions/types';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api/rovo-experience-api-store';

import { useAISmartButtonContext } from '../../../../common/context';
import { useDailyBriefSync } from '../../../../common/hooks/use-daily-brief-sync';
import { useUnreadConversationStatus } from '../../../../common/hooks/use-unread-conversation-status';
import type { AISmartButtonProps } from '../../../../common/types/shared-types';
import { isBrowser } from '../../../../common/utils/is-browser';
/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
import { AIActionShortcutMenu } from '../../../ai-action-shortcut-menu';
import { AIButton } from '../../../ai-button';
import { AIProactiveNudge } from '../../../ai-proactive-nudge';
import { GlowAnimation } from '../glow-animation';

const i18n = defineMessages({
	tooltipLabel: {
		id: 'ai-smart-button.tooltip.label.v2',
		defaultMessage: 'Ask Rovo AI',
		description: 'Label for the AI smart button tooltip',
	},
	tooltipUnreadSuffix: {
		id: 'ai-smart-button.tooltip.unread-suffix.v2',
		defaultMessage: '{count, plural, one {# unread chat} other {# unread chats}}',
		description:
			'Suffix appended to the tooltip when there are unread conversations, e.g. "3 unread chats"',
	},
	tooltipInProgress: {
		id: 'ai-smart-button.tooltip.in-progress.v2',
		defaultMessage: '{count, plural, one {# task in progress} other {# tasks in progress}}',
		description:
			'Second line of the tooltip shown when there are in-progress tasks, e.g. "1 task in progress"',
	},
});

const styles = cssMap({
	buttonWrapper: {
		position: 'relative',
	},
	tooltipContent: {
		textAlign: 'left',
	},
	tooltipInProgressLine: {
		fontStyle: 'italic',
	},
	pillStyle: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'end',
		position: 'relative',
		borderRadius: token('radius.xxlarge'),
		backgroundColor: token('color.background.neutral.bold'),
		// @ts-expect-error TS2322 raw box-shadow string not in ADS shadow token union
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		boxShadow: '0 0 1px 0 rgba(30, 31, 33, 0.31), 0 8px 12px 0 rgba(30, 31, 33, 0.15)',
		transform: 'scale(1)',
		transformOrigin: 'right center',
		transition: 'transform 0.2s ease-in-out',
		willChange: 'transform',

		'&:hover': {
			transform: 'scale(1.125)',
		},
		'&:active': {
			transform: 'scale(1.125)',
		},
		// Grow the pill open when the container is hovered or holds focus (native CSS, no JS state).
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- descendant reveal driven by parent hover/focus state
		'&:hover [data-expandable-pill], &:focus-within [data-expandable-pill]': {
			gridTemplateColumns: '1fr',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- descendant reveal driven by parent hover/focus state
		'&:hover [data-expandable-pill-clip], &:focus-within [data-expandable-pill-clip]': {
			transform: 'translateX(0)',
		},
	},
	expandPill: {
		display: 'grid',
		gridTemplateColumns: '0fr',
		overflow: 'hidden',
		transition: 'grid-template-columns 0.2s ease-in-out',
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none',
		},
	},
	expandPillClip: {
		whiteSpace: 'noWrap',
		minWidth: 0,
		transform: 'translateX(100%)',
		willChange: 'transform',
		transition: 'transform 0.2s ease-in-out',
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none',
		},
	},
	expandPillInteractive: {
		appearance: 'none',
		border: 'none',
		cursor: 'pointer',
		backgroundColor: 'transparent',
		font: token('font.body.small'),
		color: token('color.text.inverse'),
		'&:hover': {
			// @ts-expect-error TS2322 opacity 0.9 not in allowed opacity union (0 | 1 | tokens)
			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
			opacity: 0.9,
		},
	},
	// Inner column so the 2-line label fits within the 48px pill height.
	expandPillInner: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'flex-end',
		whiteSpace: 'nowrap',
		font: token('font.body.small'),
		color: token('color.text.inverse'),
	},
	expandedPillContent: {
		color: token('color.text.inverse'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'start',
		paddingInlineStart: token('space.150'),
	},
});

const EDIT_PAGE_MODE = 'edit';

type AISmartButtonUIProps = Pick<AISmartButtonProps, 'isDisabled'> & {
	onChatHistoryClick?: () => void;
	onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	onMouseEnter: () => void;
	onMouseLeave: () => void;
	/**
	 * Mouse-proximity animation key (TREX-1527). Bumped by AISmartButton when
	 * the cursor enters the proximity radius; remounting the animated logo
	 * restarts its SMIL color-cycle.
	 */
	proximityAnimationKey?: number;
};

export const AISmartButtonUI: React.ForwardRefExoticComponent<
	Pick<AISmartButtonProps, 'isDisabled'> & {
		onChatHistoryClick?: () => void;
		onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
		onMouseEnter: () => void;
		onMouseLeave: () => void;
		proximityAnimationKey?: number;
	} & React.RefAttributes<HTMLButtonElement>
> = forwardRef<HTMLButtonElement, AISmartButtonUIProps>(
	(
		{ onClick, isDisabled, onMouseEnter, onMouseLeave, proximityAnimationKey, onChatHistoryClick },
		ref: Ref<HTMLButtonElement>,
	) => {
		const {
			sidebarApi,
			analyticsAttributes,
			product,
			productPage,
			pageMode,
			getActions,
			onAnchorElementReady,
		} = useAISmartButtonContext();

		// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
		const isUnreadIndicatorEnabled = FeatureGates.getExperimentValue<boolean>(
			'rovo_button_unread_indicator',
			'isEnabled',
			false,
		);
		const isDailyBriefEnabled = fg('rovo_button_daily_brief_nudge');
		const { hasUnread, unreadCount, inProgressCount } = useUnreadConversationStatus({
			enabled: isUnreadIndicatorEnabled,
		});
		const { mostRecentUnread } = useUnreadConversationStatus({
			enabled: isDailyBriefEnabled,
		});
		useDailyBriefSync({ enabled: isDailyBriefEnabled, mostRecentUnread });
		const { formatMessage } = useIntl();
		const { toggleConversationAssistantUIMode } = useRovoExperienceAPIStore();

		const [canShowNudge, setCanShowNudge] = useState(false);
		const [spotlightRendered, setSpotlightRendered] = useState(false);
		const [hasActiveNudge, setHasActiveNudge] = useState(false);
		const nudgeCounterRef = useRef(0);
		const handleActiveNudgeChange = useCallback((active: boolean) => {
			setHasActiveNudge((prev) => {
				if (active && !prev) {
					nudgeCounterRef.current += 1;
				}
				return active;
			});
		}, []);
		const [isHovering, setIsHovering] = useState(false);
		const [actionShortcuts, setActionShortcuts] = useState<ConversationAction[]>([]);
		const placementElementRef = useRef<HTMLDivElement | null>(null);
		const notifiedSpotlightAnchorIdRef = useRef<string>();
		const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

		const onboardingSpotlightElementId = useMemo(() => {
			const isLiveOrEditPage =
				analyticsAttributes?.pageMode === 'live' || analyticsAttributes?.pageMode === 'edit';
			const isPageOrBlogpost = productPage === 'page' || productPage === 'blogpost';

			if (isPageOrBlogpost && isLiveOrEditPage) {
				return `rovo-button-onboarding-spotlight-live-or-edit`;
			}

			return `rovo-button-onboarding-spotlight`;
		}, [productPage, analyticsAttributes]);

		useEffect(() => {
			const placementElement = placementElementRef.current;

			if (
				!placementElement?.isConnected ||
				placementElement.id !== onboardingSpotlightElementId ||
				notifiedSpotlightAnchorIdRef.current === onboardingSpotlightElementId
			) {
				return;
			}

			notifiedSpotlightAnchorIdRef.current = onboardingSpotlightElementId;
			onAnchorElementReady?.(onboardingSpotlightElementId);
		}, [onAnchorElementReady, onboardingSpotlightElementId]);

		// Fetch action shortcuts when experiment is enabled
		const slaOperationsEnabled = fg('jmb-4989_add_sla_operations_in_rovo');
		useEffect(() => {
			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
			const confluenceShortcutsEnabled =
				FeatureGates.getExperimentValue<boolean>(
					'confluence_rovo_button_shortcuts_editor_only',
					'isEnabled',
					false,
				) === true;
			const shouldFetch = getActions && (confluenceShortcutsEnabled || product === 'jira');

			if (shouldFetch) {
				getActions()
					.then((actions) => {
						// Filter to only get actions that can be used as shortcuts (Base, Chat, or Skill e.g. Jira SLA when feature-flagged)
						const shortcuts = actions.filter(
							(action) =>
								action.handleType === HandleActionType.Base ||
								action.handleType === HandleActionType.Chat ||
								(slaOperationsEnabled && action.handleType === HandleActionType.Skill),
						);
						const slicedShortcuts = shortcuts.slice(0, 3);
						setActionShortcuts(slicedShortcuts);
					})
					.catch(() => {
						setActionShortcuts([]);
					});
			}
		}, [getActions, product, slaOperationsEnabled]);

		// Handle "Get Started" button click from spotlight
		const handleGetStartedClick = useCallback(() => {
			if (sidebarApi) {
				toggleConversationAssistantUIMode({
					targetMode: 'mini-modal',
					sidebarApi,
				});
			}
		}, [sidebarApi, toggleConversationAssistantUIMode]);

		// Set up event listeners for spotlight communication
		useEffect(() => {
			if (
				!isBrowser() ||
				!placementElementRef.current ||
				(!fg('rovo_button_onboarding_spotlight') && !fg('cc_ai_growth_welcome_from_rovo_gate'))
			) {
				return;
			}

			const doc = getDocument();

			if (!doc) {
				return;
			}

			// Listen for spotlight rendered event
			const handleSpotlightRendered = (event: Event) => {
				const customEvent = event as CustomEvent;
				if (customEvent.detail?.elementId === onboardingSpotlightElementId) {
					setSpotlightRendered(true);
				}
			};

			// Listen for "Get Started" button click event
			const handleGetStartedEvent = (event: Event) => {
				const customEvent = event as CustomEvent;
				if (customEvent.detail?.elementId === onboardingSpotlightElementId) {
					handleGetStartedClick();
				}
			};

			const unbind = bindAll(doc, [
				{
					type: 'rovo-button-onboarding-spotlight-rendered',
					listener: handleSpotlightRendered,
				},
				{
					type: 'rovo-button-onboarding-spotlight-get-started-click',
					listener: handleGetStartedEvent,
				},
			]);

			return () => {
				unbind();
			};
		}, [handleGetStartedClick, onboardingSpotlightElementId]);

		useEffect(() => {
			if (spotlightRendered) {
				return;
			}

			// nudge should be able to be shown as long as rovo_onboarding_modal_confluence_chat exp is enabled
			if (
				(!fg('rovo_button_onboarding_spotlight') ||
					FeatureGates.getExperimentValue<boolean>(
						'rovo_onboarding_modal_confluence_chat',
						'isEnabled',
						false,
					)) &&
				!canShowNudge
			) {
				setCanShowNudge(true);
				return;
			}

			// Give spotlight time to render (if it will) since it needs to run decisions logic
			const timeout = setTimeout(() => {
				// nudge should be able to be shown as long as rovo_onboarding_modal_confluence_chat exp is enabled
				if (
					(!spotlightRendered ||
						FeatureGates.getExperimentValue<boolean>(
							'rovo_onboarding_modal_confluence_chat',
							'isEnabled',
							false,
						)) &&
					!canShowNudge
				) {
					setCanShowNudge(true);
				}
			}, 1000);

			return () => clearTimeout(timeout);
		}, [spotlightRendered, canShowNudge]);

		/* eslint-disable @atlaskit/platform/no-preconditioning -- intentional gate + experiment layering */
		const nudgeOrPlacement = useMemo(() => {
			return (
				<React.Fragment>
					{((fg('rovo_button_onboarding_spotlight') &&
						!FeatureGates.getExperimentValue<boolean>(
							'rovo_onboarding_modal_confluence_chat',
							'isEnabled',
							false,
						)) ||
						fg('cc_ai_growth_welcome_from_rovo_gate')) && (
						<div id={onboardingSpotlightElementId} ref={placementElementRef} />
					)}
					{canShowNudge && <AIProactiveNudge onActiveNudgeChange={handleActiveNudgeChange} />}
				</React.Fragment>
			);
		}, [canShowNudge, handleActiveNudgeChange, onboardingSpotlightElementId]);
		/* eslint-enable @atlaskit/platform/no-preconditioning */

		const handleButtonMouseEnter = useCallback(() => {
			clearTimeout(hoverTimeoutRef.current);
			hoverTimeoutRef.current = setTimeout(() => {
				setIsHovering(true);
			}, 250);
			void import('@atlassian/conversation-assistant/preload-chat-on-intent')
				.then((m) => m.preloadChatOnIntent())
				.catch(() => {});
			onMouseEnter();
		}, [onMouseEnter]);

		const handleButtonMouseLeave = useCallback(() => {
			clearTimeout(hoverTimeoutRef.current);
			hoverTimeoutRef.current = setTimeout(() => {
				setIsHovering(false);
				onMouseLeave();
			}, 1000);
		}, [onMouseLeave]);

		const handleActionBoxMouseEnter = useCallback(() => {
			clearTimeout(hoverTimeoutRef.current);
			setIsHovering(true);
		}, []);

		const handleActionBoxMouseLeave = useCallback(() => {
			clearTimeout(hoverTimeoutRef.current);
			hoverTimeoutRef.current = setTimeout(() => {
				setIsHovering(false);
			}, 1000);
		}, []);

		const canShowActionShortcuts = useMemo(() => {
			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
			const confluenceShortcutsEnabled =
				FeatureGates.getExperimentValue<boolean>(
					'confluence_rovo_button_shortcuts_editor_only',
					'isEnabled',
					false,
				) === true;
			const confluenceEditMode =
				pageMode?.toLowerCase() === EDIT_PAGE_MODE && confluenceShortcutsEnabled;
			const jiraWithShortcuts =
				slaOperationsEnabled && product === 'jira' && actionShortcuts.length > 0;
			return (
				(actionShortcuts.length > 0 &&
					!hasActiveNudge &&
					!spotlightRendered &&
					(confluenceEditMode || jiraWithShortcuts)) === true
			);
		}, [
			actionShortcuts.length,
			hasActiveNudge,
			spotlightRendered,
			pageMode,
			product,
			slaOperationsEnabled,
		]);

		const actionShortcutsRendering = useMemo(() => {
			if (!isHovering || !canShowActionShortcuts) {
				return null;
			}

			return (
				<AIActionShortcutMenu
					actionShortcuts={actionShortcuts}
					onMouseEnter={handleActionBoxMouseEnter}
					onMouseLeave={handleActionBoxMouseLeave}
				/>
			);
		}, [
			isHovering,
			canShowActionShortcuts,
			actionShortcuts,
			handleActionBoxMouseEnter,
			handleActionBoxMouseLeave,
		]);

		// Shared label node reused by BOTH the popover tooltip (gate OFF) and the
		// inline expand label (gate ON) so the text/logic stays identical.
		const labelContent = (
			<Box xcss={styles.tooltipContent}>
				<Box>
					{hasUnread
						? `${formatMessage(i18n.tooltipUnreadSuffix, { count: unreadCount })}`
						: formatMessage(i18n.tooltipLabel)}
				</Box>
				{inProgressCount > 0 && (
					<Box xcss={styles.tooltipInProgressLine}>
						{formatMessage(i18n.tooltipInProgress, { count: inProgressCount })}
					</Box>
				)}
			</Box>
		);

		// Gate-ON path: the visible inline label is aria-hidden, so provide an
		// SR-only text node carrying the SAME unread/in-progress information the
		// old tooltip announced.
		const accessibleLabelText = [
			hasUnread
				? `${formatMessage(i18n.tooltipUnreadSuffix, { count: unreadCount })}`
				: formatMessage(i18n.tooltipLabel),
			inProgressCount > 0
				? formatMessage(i18n.tooltipInProgress, { count: inProgressCount })
				: null,
		]
			.filter(Boolean)
			.join('. ');

		return (
			<Inline space="space.100" alignBlock="center" xcss={styles.buttonWrapper}>
				{nudgeOrPlacement}
				{isUnreadIndicatorEnabled ? (
					<RovoButtonExpander
						expandedContent={
							<Box xcss={styles.expandedPillContent}>
								{hasUnread ? (
									<Pressable
										testId="rovo-button-expanded-click"
										xcss={styles.expandPillInteractive}
										data-rovo-expand-pill
										aria-label={accessibleLabelText}
										onClick={() => {
											onChatHistoryClick?.();
										}}
									>
										<Box xcss={styles.expandPillInner}>{labelContent}</Box>
									</Pressable>
								) : (
									<Box xcss={styles.expandPillInner}>{labelContent}</Box>
								)}
							</Box>
						}
					>
						<AIButton
							onClick={onClick}
							isDisabled={isDisabled}
							hasUnread={hasUnread}
							onMouseEnter={handleButtonMouseEnter}
							onMouseLeave={handleButtonMouseLeave}
							ref={ref}
							proximityAnimationKey={proximityAnimationKey}
							isExpandable={true}
						/>
					</RovoButtonExpander>
				) : (
					<Tooltip
						position="left"
						content={
							<Box xcss={styles.tooltipContent}>
								<Box>
									{hasUnread
										? `${formatMessage(i18n.tooltipLabel)} • ${formatMessage(i18n.tooltipUnreadSuffix, { count: unreadCount })}`
										: formatMessage(i18n.tooltipLabel)}
								</Box>
								{inProgressCount > 0 && (
									<Box xcss={styles.tooltipInProgressLine}>
										{formatMessage(i18n.tooltipInProgress, { count: inProgressCount })}
									</Box>
								)}
							</Box>
						}
						hideTooltipOnClick
						ignoreTooltipPointerEvents
						canAppear={() => {
							// If control or not on editor surface, always show tooltip when no active nudge
							// If treatment and on editor surface, show tooltip only when shortcuts cannot be shown
							return !hasActiveNudge && !canShowActionShortcuts;
						}}
					>
						<AIButton
							onClick={onClick}
							isDisabled={isDisabled}
							hasUnread={hasUnread}
							onMouseEnter={handleButtonMouseEnter}
							onMouseLeave={handleButtonMouseLeave}
							ref={ref}
							proximityAnimationKey={proximityAnimationKey}
						/>
					</Tooltip>
				)}
				{hasActiveNudge &&
					// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
					FeatureGates.getExperimentValue('rovo_button_nudge_glow_up_exp', 'isEnabled', false) && (
						<GlowAnimation key={nudgeCounterRef.current} />
					)}
				<ExitingPersistence appear>{actionShortcutsRendering}</ExitingPersistence>
			</Inline>
		);
	},
);

function RovoButtonExpander({
	children,
	expandedContent,
}: {
	children: React.ReactNode;
	expandedContent?: React.ReactNode;
}) {
	return (
		<Box xcss={styles.pillStyle}>
			<Box xcss={styles.expandPill} data-expandable-pill>
				<Box xcss={styles.expandPillClip} data-expandable-pill-clip>
					{expandedContent}
				</Box>
			</Box>
			{children}
		</Box>
	);
}
